import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import AuthClient from "../api/AuthClient";
import AcceptedApi from "../api/AcceptedApi";
import AlsoGoingOutGroupsApi from "../api/AlsoGoingOutGroupsApi";
import GetHostChatMessages from "../api/GetHostChatMessagesApi";
import GroupsClient from "../api/GroupsClient";
import NewSeenGroupsApi from "../api/NewSeenTabApi";
import OpenClubListingsApi from "../api/OpenClubListingsApi";
import PromoterGroupsApi from "../api/PromoterGroupsApi";
import BookingType from "../api/enums/BookingType";
import SortPartyGroupsByDate from "../SortPartyGroupsByDate";
import { HostChatMessage, PartyModel } from "../types";
import { getCurrentCity } from "../utils/currentCity";
import { queryKeys } from "../query/queryKeys";

const getAuthClient = () => new AuthClient();

const ensureSuccessfulResponse = <T,>(
  response: { statusCode: number; data?: T; errorMessage?: string } | undefined,
  fallbackMessage: string
): T => {
  if (!response || response.statusCode >= 400) {
    throw new Error(response?.errorMessage || fallbackMessage);
  }

  return response.data as T;
};

export const usePromoterGroups = () => {
  const authClient = getAuthClient();
  const cityId = getCurrentCity(authClient.getAuthenticationResponse())?.cityId;

  return useQuery({
    queryKey: queryKeys.promoterGroups(cityId),
    enabled: authClient.isLoggedIn(),
    queryFn: async () => {
      const api = new NewSeenGroupsApi(authClient);
      const response = await api.getMyNewParties();
      const data = ensureSuccessfulResponse(
        response,
        "Unable to fetch promoter groups."
      );

      return data;
    },
  });
};

export const useAcceptedGroups = () => {
  const authClient = getAuthClient();

  return useQuery({
    queryKey: queryKeys.acceptedGroups(),
    enabled: authClient.isLoggedIn(),
    queryFn: async () => {
      const api = new AcceptedApi(authClient);
      const response = await api.getMyParties();
      const data = ensureSuccessfulResponse(
        response,
        "Unable to fetch accepted groups."
      );

      return data;
    },
  });
};

export const usePromoterGroup = (groupId?: string) => {
  const authClient = getAuthClient();

  return useQuery({
    queryKey: queryKeys.promoterGroup(groupId),
    enabled: authClient.isLoggedIn() && Boolean(groupId),
    queryFn: async () => {
      if (!groupId) {
        throw new Error("Group ID is required.");
      }

      const api = new PromoterGroupsApi(authClient);
      const response = await api.getPromoterGroup(groupId);
      const data = ensureSuccessfulResponse(
        response,
        "Unable to fetch promoter group."
      );

      return data;
    },
  });
};

export const getMyPartiesQueryOptions = () => {
  const authClient = getAuthClient();

  return {
    queryKey: queryKeys.myParties(),
    enabled: authClient.isLoggedIn(),
    queryFn: async () => {
      const groupsClient = new GroupsClient(authClient);
      const response = await groupsClient.getParsedMyParties();
      const data = ensureSuccessfulResponse(
        response,
        "Unable to fetch parties."
      );

      return SortPartyGroupsByDate(data);
    },
  };
};

export const useMyParties = () => {
  return useQuery(getMyPartiesQueryOptions());
};

export const getMyPartyQueryOptions = (
  groupId: string | undefined,
  queryClient: QueryClient
) => {
  const authClient = getAuthClient();

  return {
    queryKey: queryKeys.myParty(groupId),
    enabled: authClient.isLoggedIn() && Boolean(groupId),
    initialData: () => {
      if (!groupId) {
        return undefined;
      }

      return queryClient
        .getQueryData<PartyModel[]>(queryKeys.myParties())
        ?.find((party) => party.party.id === groupId);
    },
    queryFn: async () => {
      if (!groupId) {
        throw new Error("Group ID is required.");
      }

      const groupsClient = new GroupsClient(authClient);
      const response = await groupsClient.getParsedMyPartyById(groupId);
      return ensureSuccessfulResponse(response, "Unable to fetch party.");
    },
  };
};

export const useMyParty = (groupId?: string) => {
  const queryClient = useQueryClient();

  return useQuery(getMyPartyQueryOptions(groupId, queryClient));
};

export const useAlsoGoingOutGroups = (cityId?: string | null) => {
  const authClient = getAuthClient();

  return useQuery({
    queryKey: queryKeys.alsoGoingOutGroups(cityId),
    enabled: authClient.isLoggedIn(),
    queryFn: async () => {
      const api = new AlsoGoingOutGroupsApi(authClient);
      const response = await api.getAlsoGoingOutGroups(cityId ?? null);
      const data = ensureSuccessfulResponse(
        response,
        "Unable to fetch also going out groups."
      );

      return data;
    },
  });
};

export const useChatMessages = (offerId?: string) => {
  const authClient = getAuthClient();

  return useQuery<HostChatMessage[]>({
    queryKey: queryKeys.chatMessages(offerId),
    enabled: authClient.isLoggedIn() && Boolean(offerId),
    queryFn: async () => {
      if (!offerId) {
        throw new Error("Offer ID is required.");
      }

      const api = new GetHostChatMessages(authClient);
      const response = await api.getChatMessages(offerId);

      if (response.statusCode >= 400) {
        throw new Error(response.errorMessage || "Unable to fetch chat messages.");
      }

      return response.data ?? [];
    },
  });
};

export const useOpenClubListings = (
  city?: string,
  partyDate?: Date,
  bookingType?: BookingType
) => {
  const authClient = getAuthClient();

  return useQuery({
    queryKey: queryKeys.openClubListings(city, partyDate, bookingType),
    enabled:
      authClient.isLoggedIn() &&
      Boolean(city) &&
      Boolean(partyDate) &&
      bookingType !== undefined,
    queryFn: async () => {
      if (!city || !partyDate || bookingType === undefined) {
        throw new Error("Club listing query parameters are required.");
      }

      const api = new OpenClubListingsApi(authClient);
      const response = await api.getOpenClubListings(city, partyDate, bookingType);
      return ensureSuccessfulResponse(
        response,
        "Unable to fetch open club listings."
      );
    },
  });
};
